import { useCallback, useState } from "react";
import { FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Field, NativeSelect, Empty, Loading } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const SYNONYMS = {
  first_name: ["nome"], last_name: ["cognome"], fiscal_code: ["codicefiscale", "cf", "codfiscale", "codfisc"],
  birth_date: ["datadinascita", "datanascita", "natoil"], birth_place: ["luogodinascita", "luogonascita", "natoa"],
  nationality: ["nazionalita", "cittadinanza"], address: ["indirizzo", "residenza"], phone: ["cellulare", "telefono", "tel", "cell"],
  email: ["email", "mail"], iban: ["iban"], hire_date: ["dataassunzione", "assunzione", "assuntoil"],
  qualification: ["mansione", "qualifica", "ruolo"], ccnl_level: ["livello", "livelloccnl"], hourly_gross: ["costoorariolordo", "lordo", "pagaoraria"],
  hourly_company_cost: ["costoaziendale", "costoaziendaleorario", "costoorario"], dpi_size: ["tagliadpi", "taglia"],
  medical_fitness: ["idoneita", "idoneitamedica"], medical_expiry: ["scadenzaidoneita", "scadenzavisita"], notes: ["note"],
  type: ["tipo", "tipologia", "categoria"], brand: ["marca"], model: ["modello"], plate: ["targa", "matricola"],
  revision_date: ["revisione", "scadenzarevisione"], insurance_date: ["assicurazione", "scadenzaassicurazione"],
  tax_date: ["bollo", "scadenzabollo"], daily_cost: ["costogiornaliero", "costo"], maintenance: ["manutenzione", "inmanutenzione"],
};
const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

function guessMapping(fields, headers) {
  const used = new Set();
  const map = {};
  for (const f of fields) {
    const wanted = [norm(f.label), ...(SYNONYMS[f.key] || [])];
    const idx = headers.findIndex((h, i) => !used.has(i) && wanted.includes(norm(h)));
    if (idx >= 0) { map[f.key] = String(idx); used.add(idx); }
  }
  return map;
}

function buildRows(parsed, mapping) {
  return parsed.rows.map((r) => ({
    row: r.row,
    values: Object.fromEntries(Object.entries(mapping).filter(([, idx]) => idx !== "").map(([k, idx]) => [k, r.cells[Number(idx)] ?? ""])),
  }));
}

function FileStep({ parsed, busy, onFile, onSheet }) {
  return (
    <div className="surface p-5 space-y-3">
      <div className="eyebrow flex items-center gap-2"><Upload className="h-4 w-4 text-amber-400" />1 · Carica il file Excel (.xlsx)</div>
      <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid="import-file-input" />
      {busy && <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Lettura del file…</div>}
      {parsed && (
        <div className="flex flex-wrap items-end gap-3 text-sm">
          {parsed.sheets.length > 1 && (
            <Field label="Foglio"><NativeSelect value={parsed.sheet} onChange={(e) => onSheet(e.target.value)} data-testid="import-sheet-select">
              {parsed.sheets.map((s) => <option key={s}>{s}</option>)}
            </NativeSelect></Field>
          )}
          <span className="text-muted-foreground" data-testid="import-file-summary">{parsed.rows.length} righe · {parsed.headers.length} colonne nel foglio "{parsed.sheet}"</span>
        </div>
      )}
    </div>
  );
}

function MappingStep({ fields, headers, mapping, setMapping }) {
  return (
    <div className="surface p-5">
      <div className="eyebrow mb-1">2 · Associa le colonne</div>
      <p className="text-xs text-muted-foreground mb-4">Per ogni campo scegli la colonna del tuo file. I campi con * sono obbligatori. Le celle vuote non modificano i dati esistenti.</p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {fields.map((f) => (
          <Field key={f.key} label={`${f.label}${f.required ? " *" : ""}`}>
            <NativeSelect value={mapping[f.key] ?? ""} onChange={(e) => setMapping((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={`import-map-${f.key}`}>
              <option value="">— Non importare —</option>
              {headers.map((h, i) => <option key={`${i}-${h}`} value={String(i)}>{h || `Colonna ${i + 1}`}</option>)}
            </NativeSelect>
          </Field>
        ))}
      </div>
    </div>
  );
}

function PreviewTable({ fields, rows }) {
  const shown = fields.filter((f) => rows[0] && f.key in rows[0].values);
  if (!shown.length) return null;
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-xs min-w-[640px]" data-testid="import-preview-table">
        <thead><tr className="text-left uppercase text-muted-foreground border-b border-border"><th className="py-2 pr-2">Riga</th>{shown.map((f) => <th key={f.key} className="pr-2">{f.label}</th>)}</tr></thead>
        <tbody>{rows.slice(0, 5).map((r) => (
          <tr key={r.row} className="border-b border-border/50"><td className="py-1.5 pr-2 num">{r.row}</td>{shown.map((f) => <td key={f.key} className="pr-2 truncate max-w-[160px]">{r.values[f.key]}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function ResultPanel({ result, fields, busy, onCommit }) {
  const label = (k) => fields.find((f) => f.key === k)?.label || k;
  if (result.errors.length) {
    return (
      <div className="rounded-lg border border-red-700 bg-red-950/30 p-4 mt-4" data-testid="import-errors">
        <div className="font-semibold text-red-200 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" />{result.errors.length} errori: correggi il file e ricaricalo. Nessun dato è stato importato.</div>
        <ul className="text-sm space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
          {result.errors.map((e) => <li key={`${e.row}-${e.field}-${e.message}`} className="num">Riga {e.row} · {label(e.field)}: <span className="text-red-200">{e.message}</span></li>)}
        </ul>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-emerald-700 bg-emerald-950/30 p-4 mt-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="import-ready">
      <div className="flex-1 text-sm"><CheckCircle2 className="h-4 w-4 inline mr-2 text-emerald-400" />Nessun errore: <b>{result.create}</b> nuovi, <b>{result.update}</b> da aggiornare
        {result.new_qualifications?.length > 0 && <span className="text-muted-foreground"> · nuove mansioni: {result.new_qualifications.join(", ")}</span>}</div>
      <Button onClick={onCommit} disabled={busy} data-testid="import-commit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Importa {result.create + result.update} righe</Button>
    </div>
  );
}

function useImport(kind, fields) {
  const [parsed, setParsed] = useState(null);
  const [file, setFile] = useState(null);
  const [mapping, setMappingState] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const setMapping = useCallback((m) => { setMappingState(m); setResult(null); }, []);
  const parse = async (f, sheet) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (sheet) fd.append("sheet", sheet);
    try {
      const r = await api.post("/import/parse", fd);
      setFile(f);
      setParsed(r.data);
      setMapping(guessMapping(fields, r.data.headers));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const rows = parsed ? buildRows(parsed, mapping) : [];
  const send = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/import/${kind}/${action}`, { rows });
      setResult(r.data);
      return r.data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.errors) setResult({ errors: detail.errors });
      else toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    const d = await send("commit");
    if (!d) return;
    toast.success(`✓ Importazione completata: ${d.create} nuovi, ${d.update} aggiornati`);
    setParsed(null);
    setResult(null);
  };
  return { parsed, file, mapping, setMapping, rows, result, busy, parse, validate: () => send("validate"), commit };
}

function Importer({ kind, fields }) {
  const s = useImport(kind, fields);
  return (
    <div className="space-y-4">
      <FileStep parsed={s.parsed} busy={s.busy && !s.parsed} onFile={(f) => s.parse(f)} onSheet={(sh) => s.parse(s.file, sh)} />
      {s.parsed && !s.parsed.rows.length && <Empty title="Il foglio non contiene righe di dati" />}
      {s.parsed && s.parsed.rows.length > 0 && (
        <>
          <MappingStep fields={fields} headers={s.parsed.headers} mapping={s.mapping} setMapping={s.setMapping} />
          <div className="surface p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="eyebrow">3 · Anteprima e verifica</div>
              <Button variant="outline" onClick={s.validate} disabled={s.busy} data-testid="import-validate-btn">Verifica dati</Button>
            </div>
            <PreviewTable fields={fields} rows={s.rows} />
            {s.result && <ResultPanel result={s.result} fields={fields} busy={s.busy} onCommit={s.commit} />}
          </div>
        </>
      )}
    </div>
  );
}

export default function ImportPage() {
  const { user } = useAuth();
  const [kind, setKind] = useState("workers");
  const { data: fields } = useApi(user.can_import ? "/import/fields" : null);
  if (!user.can_import) return <Empty title="Funzione riservata" hint="Questa sezione è disponibile solo per l'account autorizzato." icon={Lock} />;
  return (
    <div data-testid="import-page">
      <PageHeader eyebrow="Amministrazione" title="Importa da Excel" subtitle="Carica operai o mezzi da un foglio Excel, associa le colonne e verifica i dati prima di importarli" />
      <Tabs value={kind} onValueChange={setKind} className="mb-4">
        <TabsList>
          <TabsTrigger value="workers" data-testid="import-tab-workers"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Operai</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="import-tab-vehicles"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Mezzi</TabsTrigger>
        </TabsList>
      </Tabs>
      {fields ? <Importer key={kind} kind={kind} fields={fields[kind]} /> : <Loading />}
    </div>
  );
}
